"use client";

import { useTerminalsState } from "@/States/Terminals";
import { useCommonEntitiesStore } from "@/States/commonEntities";
import { formatNumberWithDecimalPlaces } from "@/Utils/formatFunctions";
import { trpc } from "@/Utils/trpc";
import { timezoneStore } from "@/api/hooks/useConfig";
import { Card, CardComponentFilterLocation } from "@/components/pageCards/card";
import { ActiveOptions } from "@/components/pageCards/filterSort/FilterSortContext";
import { createTimeFilters } from "@/components/pageCards/filterSort/filterCreators";
import { getTimeFrame } from "@/components/pageCards/filterSort/filterTimeFrame";
import { Granularity } from "@/components/pageCards/filterSort/periodicity";
import { FilterSort } from "@/components/pageCards/filterSort/types";
import { useConfigService } from "@/pages/configuration/useConfigService";
import { useTrans } from "@/translations";

import React, { useMemo, useState } from "react";

import moment from "moment";
import { FilterTimeFrameValue } from "server/config-api/index.js";

import TenantRecyclingRateOverTimeGraph, {
  SortingRateOverTimeData,
} from "./graph";

type Props = {
  tenantId: string;
  tenantCategoryId?: string;
};

const TenantRecyclingRateOverTime: React.FC<Props> = ({
  tenantId,
  tenantCategoryId,
}) => {
  const { t } = useTrans();
  const { timezone } = timezoneStore();
  const { terminalConfig } = useConfigService();
  const { currentTerminal } = useTerminalsState();
  const { tenantCategories } = useCommonEntitiesStore();
  const [{ period: [selectedPeriod] = [] }, setActiveOptions] = useState<
    ActiveOptions<"period">
  >({} as ActiveOptions<any>);

  const defaultPeriod = useMemo(
    () =>
      terminalConfig?.defaultTimeFrame || FilterTimeFrameValue.Last6FullMonths,
    [terminalConfig],
  );

  const filters: FilterSort[] = useMemo(
    () => [
      createTimeFilters({ defaultValue: defaultPeriod }, [
        FilterTimeFrameValue.Last6FullMonths,
        FilterTimeFrameValue.LastFullYear,
      ]),
    ],
    [defaultPeriod],
  );

  const periodFilterValue = useMemo(
    () =>
      getTimeFrame(
        selectedPeriod?.value as FilterTimeFrameValue,
        defaultPeriod,
      ),
    [selectedPeriod, defaultPeriod],
  );

  const timeFrameWithCurrentMonth = useMemo(
    () => ({
      startTimeISO: periodFilterValue.startTimeISO,
      endTimeISO: moment().tz(timezone, true).toISOString(),
    }),
    [periodFilterValue, timezone],
  );

  const { data: customerPeriodsData, isLoading: isCustomerPeriodsLoading } =
    trpc.dwh.customers.getPeriods.useQuery(
      {
        ...timeFrameWithCurrentMonth,
        terminalId: currentTerminal?.id || "",
        customerId: tenantId,
        granularity: Granularity.MONTH,
        timeZone: timezone,
      },
      {
        queryKeyHashFn: () =>
          [
            "customerPeriods",
            timeFrameWithCurrentMonth.startTimeISO,
            timeFrameWithCurrentMonth.endTimeISO,
            currentTerminal?.id,
            tenantId,
            timezone,
          ].toString(),
      },
    );

  const { data: buildingSortingData, isLoading: isBuildingSortingLoading } =
    trpc.dwh.terminals.getPeriods.useQuery(
      {
        ...timeFrameWithCurrentMonth,
        timeZone: timezone,
        terminalId: currentTerminal?.id || "",
        granularity: Granularity.MONTH,
        tenantCategory: tenantCategoryId || "",
      },
      {
        queryKeyHashFn: () =>
          [
            "terminalPeriods",
            timeFrameWithCurrentMonth.startTimeISO,
            timeFrameWithCurrentMonth.endTimeISO,
            timezone,
            currentTerminal?.id,
            tenantCategoryId,
          ].toString(),
        enabled: !!currentTerminal && !!tenantCategoryId,
      },
    );

  const { data: clientSortingData, isLoading: isClientSortingLoading } =
    trpc.dwh.clients.getAggregatesSortingDegrees.useQuery(
      {
        ...timeFrameWithCurrentMonth,
        granularity: Granularity.MONTH,
        tenantCategory: tenantCategoryId || "",
        timeZone: timezone,
      },
      {
        queryKeyHashFn: () =>
          [
            "clientSortingData",
            timeFrameWithCurrentMonth.startTimeISO,
            timeFrameWithCurrentMonth.endTimeISO,
            timezone,
            tenantCategoryId,
            timezone,
          ].toString(),
        enabled: !!tenantCategoryId && terminalConfig?.showIndustryAverage,
      },
    );

  const chartData = useMemo(() => {
    if (!customerPeriodsData) {
      return [];
    }

    const { periods } = customerPeriodsData;
    let actualPeriods = [];
    const nrPeriods = periods.length;
    const currentMonthData = periods[nrPeriods - 1];
    if (currentMonthData?.customer.totalOccurrences > 0) {
      actualPeriods = periods.slice(1); // remove first month to keep nr months consistent
    } else {
      actualPeriods = periods.slice(0, -1); // remove current month because it has no data
    }

    return actualPeriods.map((period) => {
      const sortingDegree = period.customer?.sortingDegree ?? 0;

      const data: SortingRateOverTimeData = {
        name: moment(period.startTime).tz(timezone).format("MMM"),
        value: sortingDegree,
        sortingRate: formatNumberWithDecimalPlaces(sortingDegree * 100, 2),
        percentLabel: `${formatNumberWithDecimalPlaces(sortingDegree * 100, 2)}%`,
      };

      if (tenantCategoryId) {
        if (terminalConfig?.showIndustryAverage) {
          const clientSortingPeriod = clientSortingData?.sortingDegrees?.find(
            (p) => p.startTime === period.startTime,
          );
          const clientSortingDegree = clientSortingPeriod?.sortingDegree || 0;
          data.clientSortingRate = formatNumberWithDecimalPlaces(
            clientSortingDegree * 100,
            2,
          );
        }

        const buildingSortingPeriod = buildingSortingData?.periods.find(
          (p) => p.startTime === period.startTime,
        );
        const buildingSortingDegree =
          buildingSortingPeriod?.terminal.sortingDegree || 0;
        data.buildingSortingRate = formatNumberWithDecimalPlaces(
          buildingSortingDegree * 100,
          2,
        );
      }

      return data;
    });
  }, [
    clientSortingData,
    customerPeriodsData,
    tenantCategoryId,
    timezone,
    buildingSortingData,
    terminalConfig,
  ]);

  const tenantCategoryName = useMemo(
    () => tenantCategories?.find((c) => c.id === tenantCategoryId)?.name || "",
    [tenantCategories, tenantCategoryId],
  );

  return (
    <Card
      title={t("circularityLabels:tenantRecyclingRateOverTime")}
      filters={filters}
      filterLocation={CardComponentFilterLocation.RIGHT}
      onFilterOptionChange={setActiveOptions}
      isLoading={
        isCustomerPeriodsLoading ||
        (!!tenantCategoryId &&
          ((terminalConfig?.showIndustryAverage && isClientSortingLoading) ||
            isBuildingSortingLoading))
      }
      className="min-w-[500px]"
    >
      <TenantRecyclingRateOverTimeGraph
        data={chartData}
        terminalName={currentTerminal?.name || ""}
        tenantCategoryName={tenantCategoryName}
        showIndustryAverage={terminalConfig?.showIndustryAverage}
      />
    </Card>
  );
};

export default TenantRecyclingRateOverTime;
